import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useDocumentResultsInteractions } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsInteractions'
import { useDocumentResultsKeyboardNavigation } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsKeyboardNavigation'
import { useDocumentResultsViewState } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentResultsViewState'
import { ContextMenu } from 'features/knowledgeDocumentViewer/components/ContextMenu'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import React from 'react'

import { DocumentResultsContent } from './DocumentResultsContent'
import {
  UnifiedSearchHeader,
  type UnifiedSearchHeaderProps,
} from './UnifiedSearchHeader'

type DocumentResultsSectionProps = Omit<
  UnifiedSearchHeaderProps,
  'onViewModeChange'
>

export function DocumentResultsSection({
  ...headerProps
}: DocumentResultsSectionProps): React.ReactElement {
  const { activeTabId, isGeckoUser } = useKnowledgeDocumentUXContext()
  const {
    onSelectDocument,
    onOpenNewTab,
    onOpenChatbot,
    onAddRecentDocument,
    onSnapshotSearchResults,
  } = useSearchActions()
  const {
    browseAllDisplay,
    documentResults,
    documentTotalCount,
    isLoadingDocuments,
    documentsLoadError,
    elasticsearchHighlightsMap,
    pageClassificationsMap,
    drawingRevisionGroupsMap,
    documentRowsById,
    rawSearchData,
    searchEngine,
    searchLatencyMs,
    searchedSystemDocuments,
  } = useDocumentSearchData()
  const {
    siteId,
    searchState,
    contentFilter,
    viewMode,
    selectedDocumentId,
    pagination,
    sorting,
    setPagination,
    setSorting,
    setSelectedDocumentId,
    onSearchStateChange,
    handleViewModeChange,
    isLinkedDocsMode,
    isActive,
    leftPaneWidthPercent,
    setLeftPaneWidthPercent,
  } = useAdvancedSearchUX()

  const documentResultsViewState = useDocumentResultsViewState({
    activeTabId,
    contentFilter,
    documentResults,
    facetColumnValues: browseAllDisplay?.facetColumnValues,
    handleViewModeChange,
    isGeckoUser,
    isLinkedDocsMode,
    onAddRecentDocument,
    onOpenNewTab,
    onSearchStateChange,
    onSelectDocument,
    onSnapshotSearchResults,
    rawSearchData,
    searchEngine,
    searchLatencyMs,
    searchState,
    selectedDocumentId,
    setSelectedDocumentId,
    siteId,
    viewMode,
  })
  const {
    activeDocument,
    filteredDocuments,
    handleOpenPreview,
    onSearchDocumentNavigate,
    onViewModeChange,
    setActiveIndex,
  } = documentResultsViewState

  const documentResultsInteractions = useDocumentResultsInteractions({
    onSearchDocumentNavigate,
    onOpenChatbot,
    onOpenPreview: handleOpenPreview,
    onSearchStateChange,
  })
  const {
    contextMenuPosition,
    handleContextMenuClose,
    handleContextMenuOpen,
    handleContextMenuOpenInNewTab,
    handleContextMenuAddToContext,
  } = documentResultsInteractions

  useDocumentResultsKeyboardNavigation({
    activeDocument,
    documentResults,
    enabled: isActive && contentFilter === 'documents',
    filteredDocuments,
    mode: 'documents',
    onSearchDocumentNavigate,
    selectedDocumentId,
    setActiveIndex,
    setSelectedDocumentId,
    viewMode,
  })

  return (
    <>
      <DocumentResultsContent
        data={{
          documentResults,
          documentTotalCount,
          elasticsearchHighlightsMap,
          pageClassificationsMap,
          drawingRevisionGroupsMap,
          documentRowsById,
          searchedSystemDocuments,
          isLoadingDocuments,
          documentsLoadError,
        }}
        interactions={documentResultsInteractions}
        isActive={isActive}
        isLinkedDocsMode={isLinkedDocsMode}
        layout={{
          leftPaneWidthPercent,
          setLeftPaneWidthPercent,
        }}
        headerContent={
          <UnifiedSearchHeader
            {...headerProps}
            onViewModeChange={onViewModeChange}
          />
        }
        showCardFilterBar
        enablePageCardSurfacing={headerProps.isDocChatExperience}
        tableState={{
          pagination,
          setPagination,
          sorting,
          setSorting,
        }}
        viewState={documentResultsViewState}
        viewMode={viewMode}
        isServerPaged={
          searchState.browseAll === true &&
          browseAllDisplay?.loadFullFilteredSet !== true
        }
        browseAllDisplay={browseAllDisplay}
      />

      <ContextMenu
        position={contextMenuPosition}
        onClose={handleContextMenuClose}
        onOpen={handleContextMenuOpen}
        onOpenInNewTab={handleContextMenuOpenInNewTab}
        onAddToContext={handleContextMenuAddToContext}
        showNavigationOptions={true}
        showAddToContext={true}
      />
    </>
  )
}
